import { useRef, useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

import { trackEvent } from '../../utils/gt';
import { WelcomeArt } from './WelcomeArt';
import { WELCOME_SLIDES } from './slides';

const SWIPE_PX = 48;

// A plain fade: NextUI's own slide-up on phones stays stuck 80px down when the device asks for reduced motion,
// which hid this full-screen dialog's buttons below the screen
const FADE = {
  variants: {
    enter: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: 'easeOut' } },
    exit: { opacity: 0, y: 0, scale: 1, transition: { duration: 0.15, ease: 'easeIn' } },
  },
};

/**
 * The welcome tour (it replaced the one-screen feature list, 29/10, as OdeParfum's): a carousel through what
 * Meu Treino does, shown once after signup (/dashboard?w=true). "Pular" closes it at any step; the last step
 * offers the first workout. Desktop: the art at left, the copy at right; phones: full screen, the art on top.
 * The arrow keys, a swipe and the dots move between steps
 */
export const WelcomeTour = ({ firstName, onDone, onCreateWorkout }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const pointerX = useRef(null);
  const last = WELCOME_SLIDES.length - 1;
  const slide = WELCOME_SLIDES[step];
  const copy = (key, options) => t(`components.WelcomeTour.${key}`, options);
  const slideCopy = (key, options) => copy(`slides.${slide.key}.${key}`, options);
  const chips = slideCopy('chips', { returnObjects: true });

  const go = (next) => setStep(Math.max(0, Math.min(last, next)));

  const close = (how) => {
    trackEvent('welcome_tour_close', `Welcome Tour - ${how} at step ${step + 1}`, 'welcome_tour', false);
    onDone();
  };

  const createWorkout = () => {
    trackEvent('welcome_tour_create', 'Welcome Tour - Create first workout', 'welcome_tour', false);
    onCreateWorkout();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight') go(step + 1);
    else if (event.key === 'ArrowLeft') go(step - 1);
  };

  const onPointerUp = (event) => {
    if (pointerX.current === null) return;
    const dx = event.clientX - pointerX.current;
    pointerX.current = null;
    if (Math.abs(dx) >= SWIPE_PX) go(step + (dx < 0 ? 1 : -1));
  };

  const title = step === 0 ? slideCopy(firstName ? 'title' : 'titleNoName', { name: firstName }) : slideCopy('title');

  return (
    <Modal
      isOpen
      onOpenChange={(open) => !open && close('dismiss')}
      placement="center"
      size="5xl"
      hideCloseButton
      motionProps={FADE}
      aria-labelledby="welcome-tour-title"
      aria-describedby="welcome-tour-body"
      classNames={{ base: 'mt-root mt-tour', backdrop: 'mt-dialog__backdrop' }}
    >
      <ModalContent>
        {() => (
          <div className="mt-tour__layout" onKeyDown={onKeyDown}>
            <div
              className="mt-tour__stage"
              onPointerDown={(event) => {
                pointerX.current = event.clientX;
              }}
              onPointerUp={onPointerUp}
              onPointerCancel={() => {
                pointerX.current = null;
              }}
            >
              <div key={slide.key} className="mt-tour__art">
                <WelcomeArt art={slide.art} />
              </div>
              <button type="button" className="mt-tour__skip" onClick={() => close(step === last ? 'close' : 'skip')}>
                {step === last ? copy('close') : copy('skip')}
              </button>
            </div>

            <div className="mt-tour__copy">
              <div key={slide.key} className="mt-tour__text" aria-live="polite">
                <h2 id="welcome-tour-title" className="mt-tour__title">
                  {title}
                  {slide.pro && (
                    <>
                      {' '}
                      <span className="mt-tour__pro">PRO</span>
                    </>
                  )}
                </h2>
                <p id="welcome-tour-body" className="mt-tour__body">
                  {slideCopy('body')}
                </p>
                {Array.isArray(chips) && chips.length > 0 && (
                  <ul className="mt-tour__chips">
                    {chips.map((chip) => (
                      <li key={chip}>{chip}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-tour__dots" role="group" aria-label={copy('steps')}>
                {WELCOME_SLIDES.map(({ key }, index) => (
                  <button
                    key={key}
                    type="button"
                    className={`mt-tour__dot${index < step ? ' mt-tour__dot--seen' : ''}`}
                    aria-label={copy('stepLabel', { n: index + 1, total: WELCOME_SLIDES.length })}
                    aria-current={index === step ? 'step' : undefined}
                    onClick={() => go(index)}
                  >
                    <span />
                  </button>
                ))}
              </div>

              <div className={`mt-tour__actions${step === last ? ' mt-tour__actions--last' : ''}`}>
                {step === last ? (
                  <>
                    <button type="button" className="mt-btn mt-btn--quiet" onClick={() => close('explore')}>
                      {copy('explore')}
                    </button>
                    <button type="button" className="mt-btn mt-btn--primary mt-tour__next" onClick={createWorkout}>
                      {copy('create')}
                      <ArrowRight aria-hidden="true" />
                    </button>
                  </>
                ) : (
                  <>
                    {step > 0 && (
                      <button type="button" className="mt-btn mt-btn--quiet" onClick={() => go(step - 1)}>
                        {copy('back')}
                      </button>
                    )}
                    <button type="button" className="mt-btn mt-btn--primary mt-tour__next" onClick={() => go(step + 1)}>
                      {step === 0 ? copy('takeTour') : copy('next')}
                      <ArrowRight aria-hidden="true" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};
